import { signal } from '@angular/core';
import { applyFacetToStats } from '../data/facets';
import type { Mon as BaseMon, Move } from '../generated/mon';
import { MAX_STAMINA } from '../types/constants';
import { ExtendedMon } from '../types/structs';

type CoreStats = Pick<
  BaseMon['stats'],
  'hp' | 'attack' | 'defense' | 'specialAttack' | 'specialDefense' | 'speed'
>;

// Callers resolve `moves` themselves, keeping moveIndex engine-aligned.
// `opts.stats` overrides the facet derivation when max stats come from an
// authoritative source (e.g. the on-chain snapshot, already faceted + leveled).
export function buildExtendedMon(
  base: BaseMon,
  level: number | undefined,
  facetId: number,
  moves: readonly Move[],
  opts?: { stats?: CoreStats; exp?: number },
): ExtendedMon {
  const faceted = opts?.stats ?? applyFacetToStats(base.stats, facetId);
  return {
    ...base,
    moves: moves as unknown as ExtendedMon['moves'],
    level,
    exp: opts?.exp,
    equippedFacet: facetId,
    stats: {
      hp: signal(faceted.hp),
      attack: signal(faceted.attack),
      defense: signal(faceted.defense),
      specialAttack: signal(faceted.specialAttack),
      specialDefense: signal(faceted.specialDefense),
      speed: signal(faceted.speed),
      stamina: signal(MAX_STAMINA),
      isKnockedOut: signal(false),
      shouldSkipTurn: signal(false),
      maxHp: faceted.hp,
      maxStamina: MAX_STAMINA,
      maxSpeed: faceted.speed,
      maxAttack: faceted.attack,
      maxDefense: faceted.defense,
      maxSpecialAttack: faceted.specialAttack,
      maxSpecialDefense: faceted.specialDefense,
      status: signal(new Set()),
      statusData: signal(new Map()),
    },
  } satisfies ExtendedMon;
}
